/**
 * B5 · Customer order tracking.
 *
 * Routes:
 *   /orders/mine       — the user's own order list
 *   /orders/:order_id  — single order tracker with lifecycle timeline
 *
 * Server is the source of truth for order status. This page ONLY
 * reads /orders/{id} and renders the state timeline + delivery info.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Package, Truck, CheckCircle2, XCircle, RefreshCw, Clock } from "lucide-react";


const STATE_ORDER = [
  "draft", "pending_payment", "paid", "confirmed", "processing",
  "ready", "dispatched", "delivered", "completed",
];

const STATE_LABELS = {
  draft: "Draft",
  pending_payment: "Pending payment",
  paid: "Paid",
  confirmed: "Confirmed",
  processing: "Processing",
  ready: "Ready",
  dispatched: "Dispatched",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
  payment_failed: "Payment failed",
  refund_pending: "Refund pending",
  refunded: "Refunded",
};

function StateBadge({ state }) {
  const failure = ["cancelled", "payment_failed", "refunded"].includes(state);
  const pending = ["pending_payment", "refund_pending"].includes(state);
  const cls = failure ? "bg-rose-50 text-rose-700 border-rose-200"
              : pending ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${cls}`}
          data-testid={`order-state-${state}`}>
      {STATE_LABELS[state] || state}
    </span>
  );
}


export function OrdersMine() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/orders/mine")
      .then(r => setItems(r.data.orders || []))
      .catch(() => toast.error("Could not load orders"))
      .finally(() => setBusy(false));
  }, []);
  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="orders-mine">
      <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => nav(-1)} aria-label="Back"><ChevronLeft size={20}/></button>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-black">Me</div>
            <div className="font-black text-stone-900 text-base flex items-center gap-2"><Package size={18}/> My orders</div>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto p-4 space-y-2">
        {busy && <div className="text-center py-10 text-stone-400">Loading…</div>}
        {!busy && !items.length && (
          <div className="text-center py-16 text-stone-400" data-testid="orders-empty">
            <Package size={32} className="inline"/>
            <div className="mt-2 text-sm font-black">You don't have any orders yet</div>
          </div>
        )}
        {items.map(o => (
          <Link key={o.order_id} to={`/orders/${o.order_id}`}
                data-testid={`order-row-${o.order_id}`}
                className="rounded-2xl bg-white border border-stone-200 p-3 flex items-center gap-3 hover:border-emerald-300">
            <div className="w-10 h-10 rounded-xl bg-stone-100 text-stone-700 grid place-items-center">
              <Package size={18}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-stone-900 text-sm truncate">Order {o.order_id.slice(-8)}</div>
              <div className="text-[11px] text-stone-500 truncate">
                {o.currency} {(o.amount_paise/100).toFixed(2)} · {new Date(o.created_at).toLocaleDateString()}
              </div>
            </div>
            <StateBadge state={o.status}/>
          </Link>
        ))}
      </div>
    </div>
  );
}


export default function OrderDetail() {
  const { order_id } = useParams();
  const nav = useNavigate();
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/orders/${order_id}`)
      .then(r => setO(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load order"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [order_id]);

  const cancel = async () => {
    if (!window.confirm("Cancel this order?")) return;
    setBusy(true);
    try {
      await api.patch(`/orders/${order_id}/status`, { to_state: "cancelled", reason: "customer cancelled" });
      toast.success("Order cancelled"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot cancel");
    } finally { setBusy(false); }
  };
  const confirmDelivery = async () => {
    setBusy(true);
    try {
      await api.patch(`/orders/${order_id}/status`, { to_state: "completed", reason: "buyer confirmed receipt" });
      toast.success("Marked as completed"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot complete");
    } finally { setBusy(false); }
  };

  if (!o) return <div className="p-10 text-center text-stone-400">Loading…</div>;
  const idx = STATE_ORDER.indexOf(o.status);
  const dl = o.delivery || {};
  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="order-detail">
      <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => nav(-1)}><ChevronLeft size={20}/></button>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Order</div>
            <div className="font-black text-stone-900 text-base font-mono">{o.order_id}</div>
          </div>
          <StateBadge state={o.status}/>
        </div>
      </div>
      <div className="max-w-3xl mx-auto p-4 space-y-3">
        {/* Amount */}
        <div className="rounded-2xl bg-white border border-stone-200 p-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-black">Total</div>
          <div className="text-3xl font-black text-stone-900 mt-1" data-testid="order-amount">
            {o.currency} {(o.amount_paise/100).toFixed(2)}
          </div>
        </div>

        {/* Lifecycle */}
        <div className="rounded-2xl bg-white border border-stone-200 p-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-black mb-3">Progress</div>
          <div className="grid grid-cols-3 sm:grid-cols-9 gap-1">
            {STATE_ORDER.map((s, i) => {
              const reached = idx >= i && !["cancelled", "payment_failed", "refunded"].includes(o.status);
              return (
                <div key={s} className="flex flex-col items-center gap-1">
                  <div className={`w-6 h-6 rounded-full grid place-items-center text-[10px] font-black ${
                    reached ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-400"
                  }`}>{i+1}</div>
                  <div className={`text-[9px] text-center font-black ${reached ? "text-stone-800" : "text-stone-400"}`}>
                    {STATE_LABELS[s].split(" ")[0]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Delivery */}
        <div className="rounded-2xl bg-white border border-stone-200 p-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-black mb-2 flex items-center gap-1">
            <Truck size={12}/> Delivery
          </div>
          <div className="text-sm text-stone-800 space-y-0.5">
            <div><span className="text-stone-500 text-xs">Method: </span><b>{dl.method || "pickup"}</b></div>
            {dl.address && <div><span className="text-stone-500 text-xs">Address: </span>{dl.address}{dl.city ? `, ${dl.city}` : ""}</div>}
            {dl.tracking_ref && <div><span className="text-stone-500 text-xs">Tracking: </span>
              <span className="font-mono" data-testid="order-tracking-ref">{dl.tracking_ref}</span>
              {dl.tracking_url && <> · <a className="text-emerald-700 underline" href={dl.tracking_url} target="_blank" rel="noreferrer">Open</a></>}
            </div>}
            {dl.eta_iso && <div><span className="text-stone-500 text-xs">ETA: </span>{new Date(dl.eta_iso).toLocaleString()}</div>}
          </div>
        </div>

        {/* Line items */}
        <div className="rounded-2xl bg-white border border-stone-200 p-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-black mb-2">Items</div>
          {(o.lines || []).map((l, i) => (
            <div key={i} className="flex items-center justify-between text-sm border-b border-stone-100 last:border-0 py-1.5">
              <div className="min-w-0">
                <div className="font-black text-stone-900 truncate">{l.name}</div>
                <div className="text-[11px] text-stone-500">qty {l.qty} · {l.kind}</div>
              </div>
              <div className="text-stone-800 font-black">{o.currency} {(l.qty * l.unit_price_paise/100).toFixed(2)}</div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {["pending_payment", "paid", "confirmed", "processing"].includes(o.status) && (
            <button onClick={cancel} disabled={busy}
                    data-testid="order-cancel-btn"
                    className="flex-1 rounded-full bg-rose-50 text-rose-700 font-black text-xs py-2">
              <XCircle size={14} className="inline"/> Cancel
            </button>
          )}
          {o.status === "delivered" && (
            <button onClick={confirmDelivery} disabled={busy}
                    data-testid="order-complete-btn"
                    className="flex-1 rounded-full bg-emerald-600 text-white font-black text-xs py-2">
              <CheckCircle2 size={14} className="inline"/> Confirm receipt
            </button>
          )}
        </div>

        {/* Event log */}
        <details className="rounded-2xl bg-white border border-stone-200 p-4" data-testid="order-events">
          <summary className="text-xs uppercase tracking-widest text-stone-500 font-black cursor-pointer flex items-center gap-1">
            <Clock size={12}/> History
          </summary>
          <div className="mt-2 space-y-1 text-[11px] text-stone-600">
            {(o.events || []).map(ev => (
              <div key={ev.event_id} className="flex justify-between gap-2">
                <span className="font-mono">{ev.action}</span>
                <span>{ev.from_state} → {ev.to_state}</span>
                <span>{new Date(ev.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}
